import { z } from "zod";
import { PAGE_SIZE_MAX, keysetQuery, paginationQuery } from "../pagination";
import {
	type CompetitionFieldMessages,
	type CompetitorRef,
	clientIdField,
	competitionDayRangeShape,
	competitionFromBeforeTo,
	competitionNotYearZero,
	competitionSplitList,
	enCompetitionFieldMessages,
	makePoolField,
	versionField,
} from "./common";
import {
	type CompetitorType,
	type EventGender,
	type IrmCode,
	type Medal,
	type MeetOutcome,
	type MemberRole,
	OFFICIAL_ROLES,
	type OfficialRole,
	PHASE_TYPES,
	type PhaseType,
	type QualificationMark,
	RESULT_FAMILIES,
	RESULT_STATUSES,
	RESULT_TYPES,
	type ResultFamily,
	type ResultSource,
	type ResultStatus,
	type ResultType,
	SCHEDULE_STATUSES,
	STANDING_RULES,
	type ScheduleStatus,
	type SideScheme,
	type SlotSourceOutcome,
	type StandingRule,
} from "./vocab";
import { meetConfigSchema } from "./results";
import { makeStreamUrlField } from "./streams";
import type { MeetTimingFeed } from "./timing";

/**
 * The structure and schedule side of a competition — slice S3:
 *
 *   GET  /events/:id/phases           PhaseItem[]                 (the whole list: an event has a handful)
 *   POST /events/:id/phases           PhaseItem
 *   PUT  /events/:id/phases/order     PhaseItem[]                 renumbers 1..n
 *   GET  /events/:id/structure        EventStructure              phases → meets → slots, one statement
 *   PATCH / DELETE /phases/:id
 *   GET  /meets                       Keyset<MeetListItem>        the schedule
 *   GET  /meets/unscheduled           Paginated<MeetListItem>
 *   GET  /meets/status-counts         MeetStatusCount[]
 *   GET  /meets/status-counts/by-sport Paginated<MeetSportStatusCounts>   the dashboard, per sport
 *   GET  /meets/days                  Paginated<MeetDayCount>     the days a games has meets
 *   (mission control's alerts, venue board and activity: `./control`)
 *   POST /meets, POST /meets/bulk, POST /meets/bulk-action
 *   GET / PATCH / DELETE /meets/:id   MeetDetail / MeetRow
 *   PUT  /meets/:id/officials         MeetOfficialItem[]
 *
 * Start lists, results and line-ups are written elsewhere (`./results`); this
 * module only READS them, inside a meet's detail and the schedule's summary.
 *
 * Every key here is snake_case: these are wire shapes (CLAUDE.md, Naming).
 * Timestamps on the wire are ISO 8601 — UTC with a `Z` on the way out, any
 * explicit offset on the way in. A timestamp without a zone is refused: "09:30"
 * means a different instant in every venue's country.
 */

// ─── Limits ──────────────────────────────────────────────────────────────────

/** `meet_no` — the official number on the schedule ("204", "204.1"). */
export const MEET_NO_MAX = 20;
export const MEET_NAME_MAX = 120;
export const PHASE_NAME_MAX = 120;
export const MEET_REMARKS_MAX = 500;
/** `area_name` — the court / ring / mat typed for a meet when the hall lists none ("Ring A"). */
export const MEET_AREA_NAME_MAX = 60;
/** Meets one `POST /meets/bulk` may create — a 128-draw bracket and its rubbers fit. */
export const MEETS_BULK_MAX = 200;
/** Meets one `POST /meets/bulk-action` may touch. */
export const MEET_BULK_ACTION_MAX = 100;
/** Officials on one meet — a gymnastics panel with its reserves fits. */
export const MEET_OFFICIALS_MAX = 60;
export const MEET_OFFICIAL_NAME_MAX = 120;
/** An official's seat — "FX:E1", "R1". */
export const MEET_OFFICIAL_POSITION_MAX = 20;
/** Slots shown per meet in the schedule's competitor summary. The detail shows them all. */
export const MEET_BRIEF_COMPETITORS_MAX = 8;
/** Phases one `PUT /events/:id/phases/order` may name. An event has a handful. */
export const EVENT_PHASES_MAX = 50;

// Column limits — the most the column can hold, so a nonsense value is a 422
// from the validator rather than "integer out of range" from Postgres.
const PG_SMALLINT_MAX = 32_767;
const PG_INTEGER_MAX = 2_147_483_647;

/** The most a phase's `advance_count` (how many go on to the next stage) may be. */
export const ADVANCE_COUNT_MAX = PG_SMALLINT_MAX;

/**
 * The audit headline (`reason`) a write may carry. The same rule as the API's
 * `reasonField` (`apps/api/src/schemas/common.schema.ts`) — trimmed, 1–300 —
 * and `apps/api/tests/unit/contract-parity.meets.test.ts` pins the two to the
 * same verdict, because they are typed in two places.
 */
const reasonText = z.string().trim().min(1).max(300);

/** A uuid naming an existing row. Lower-cased for the same reason as `clientIdField`. */
const idField = z.string().uuid().toLowerCase();

// ─── Messages ────────────────────────────────────────────────────────────────

/**
 * Every human-readable message the form schemas below emit, so a frontend can
 * hand in translated copy while the API keeps the English default verbatim
 * (the same pattern as `makeCreateUserForm`).
 */
export interface MeetFormMessages extends Pick<CompetitionFieldMessages, "poolFormat"> {
	nameRequired: string;
	nameTooLong: string;
	meetNoRequired: string;
	meetNoTooLong: string;
	remarksTooLong: string;
	/** An ISO 8601 date and time WITH a zone — `2027-12-05T09:30:00+08:00`. */
	timestampFormat: string;
	endBeforeStart: string;
	parentIsSelf: string;
	meetNoRepeated: string;
	meetIdRepeated: string;
	officialWhoRequired: string;
	officialNameTooLong: string;
	positionTooLong: string;
	positionRepeated: string;
	officialIdRepeated: string;
	phaseIdRepeated: string;
	meetIdSelectedTwice: string;
	scheduleStatusRequired: string;
	confirmPasswordRequired: string;
	nothingToChange: string;
	/** A meet created with `config` must name its `result_family`: the config is checked against that family. */
	configNeedsFamily: string;
}

export const enMeetFormMessages: MeetFormMessages = {
	poolFormat: enCompetitionFieldMessages.poolFormat,
	nameRequired: "Enter a name",
	nameTooLong: "120 characters at most",
	meetNoRequired: "Enter a meet number",
	meetNoTooLong: `${MEET_NO_MAX} characters at most`,
	remarksTooLong: `${MEET_REMARKS_MAX} characters at most`,
	timestampFormat: "Enter a date and time with its time zone",
	endBeforeStart: "The end is before the start",
	parentIsSelf: "A meet cannot be its own parent",
	meetNoRepeated: "Each meet number may appear only once",
	meetIdRepeated: "Each meet id may appear only once",
	officialWhoRequired: "Choose a person or type a name",
	officialNameTooLong: `${MEET_OFFICIAL_NAME_MAX} characters at most`,
	positionTooLong: `${MEET_OFFICIAL_POSITION_MAX} characters at most`,
	positionRepeated: "Each position may be given to one official only",
	officialIdRepeated: "Each official may appear only once",
	phaseIdRepeated: "Each phase may appear only once",
	meetIdSelectedTwice: "Each meet may appear only once",
	scheduleStatusRequired: "Choose a schedule status",
	confirmPasswordRequired: "Enter your own password to confirm",
	nothingToChange: "At least one field must be provided",
	configNeedsFamily: "Choose the result family the settings are for",
};

// ─── Field rules ─────────────────────────────────────────────────────────────

/** `0000` is the one 4-digit year Postgres refuses (there is no year zero): a 422 here, not a 500 there. */
const notYearZero = competitionNotYearZero;

/**
 * An instant on the wire: ISO 8601 with `Z` or an explicit offset. Kept as the
 * STRING it arrived as — Postgres parses it into `timestamptz` with its
 * microseconds; a JS `Date` would cut them to milliseconds.
 */
const makeTimestampField = (m: Pick<MeetFormMessages, "timestampFormat">) =>
	z.string().datetime({ offset: true, message: m.timestampFormat }).refine(notYearZero, m.timestampFormat);

const makeNameField = (m: Pick<MeetFormMessages, "nameRequired" | "nameTooLong">, max: number) =>
	z.string().trim().min(1, m.nameRequired).max(max, m.nameTooLong);

const makeMeetNoField = (m: Pick<MeetFormMessages, "meetNoRequired" | "meetNoTooLong">) =>
	z.string().trim().min(1, m.meetNoRequired).max(MEET_NO_MAX, m.meetNoTooLong);

const makeRemarksField = (m: Pick<MeetFormMessages, "remarksTooLong">) =>
	z.string().trim().min(1).max(MEET_REMARKS_MAX, m.remarksTooLong);

const roundNoField = z.number().int().min(1).max(PG_SMALLINT_MAX);
const meetSequenceField = z.number().int().min(0).max(PG_INTEGER_MAX);

/** True when the later instant is before the earlier one. Both are validated ISO strings. */
const endsBeforeStart = (start: string | null | undefined, end: string | null | undefined) =>
	typeof start === "string" && typeof end === "string" && Date.parse(end) < Date.parse(start);

const repeated = (values: readonly string[]) => new Set(values).size !== values.length;

// ─── Phases: requests ────────────────────────────────────────────────────────

/**
 * `POST /events/:id/phases`. Omitted `sequence` = the event's highest + 1;
 * omitted `lower_is_better` = the event's; omitted `standing_rule` = NONE.
 * `phase_id` makes the create repeatable (the same request twice returns the
 * existing phase with 200 and writes nothing).
 */
export function makeCreatePhaseBody(m: MeetFormMessages = enMeetFormMessages) {
	return z.object({
		phase_id: clientIdField,
		type: z.enum(PHASE_TYPES),
		name: makeNameField(m, PHASE_NAME_MAX),
		sequence: z.number().int().min(1).max(PG_SMALLINT_MAX).optional(),
		standing_rule: z.enum(STANDING_RULES).optional(),
		lower_is_better: z.boolean().optional(),
		advance_count: z.number().int().min(1).max(ADVANCE_COUNT_MAX).nullable().optional(),
		advance_by_place: z.number().int().min(1).max(ADVANCE_COUNT_MAX).nullable().optional(),
		awards_medals: z.boolean().optional(),
	});
}
export const createPhaseBody = makeCreatePhaseBody();
export type CreatePhaseBody = z.input<typeof createPhaseBody>;

/**
 * `PATCH /phases/:id`. `null` clears `advance_count` / `advance_by_place`; an absent key is left
 * alone. `sequence` is not here: the order is changed with
 * `PUT /events/:id/phases/order`, which renumbers the whole event at once.
 */
export function makeUpdatePhaseBody(m: MeetFormMessages = enMeetFormMessages) {
	return z
		.object({
			type: z.enum(PHASE_TYPES).optional(),
			name: makeNameField(m, PHASE_NAME_MAX).optional(),
			standing_rule: z.enum(STANDING_RULES).optional(),
			lower_is_better: z.boolean().optional(),
			advance_count: z.number().int().min(1).max(ADVANCE_COUNT_MAX).nullable().optional(),
			advance_by_place: z.number().int().min(1).max(ADVANCE_COUNT_MAX).nullable().optional(),
			awards_medals: z.boolean().optional(),
			/** The headline for this change in the Activity feed. */
			reason: reasonText.optional(),
		})
		.refine(({ reason: _reason, ...fields }) => Object.values(fields).some((f) => f !== undefined), {
			message: m.nothingToChange,
		});
}
export const updatePhaseBody = makeUpdatePhaseBody();
export type UpdatePhaseBody = z.input<typeof updatePhaseBody>;

/**
 * `PUT /events/:id/phases/order` — EVERY phase of the event, in the new order;
 * they are renumbered 1..n. A list that is not exactly the event's phases is a
 * 409 (someone added or removed one since this client loaded the list).
 */
export function makeReorderPhasesBody(m: MeetFormMessages = enMeetFormMessages) {
	return z.object({
		phase_ids: z
			.array(idField)
			.min(1)
			.max(EVENT_PHASES_MAX)
			.refine((ids) => !repeated(ids), { message: m.phaseIdRepeated }),
	});
}
export const reorderPhasesBody = makeReorderPhasesBody();
export type ReorderPhasesBody = z.input<typeof reorderPhasesBody>;

// ─── Phases: responses ───────────────────────────────────────────────────────

/** One phase of an event — `GET /events/:id/phases`, and what a phase write returns. */
export interface PhaseItem {
	phase_id: string;
	event_id: string;
	type: PhaseType;
	name: string;
	/** 1 = the first stage played. */
	sequence: number;
	standing_rule: StandingRule;
	lower_is_better: boolean;
	advance_count: number | null;
	/** Of `advance_count`, the first N of EACH meet go through by place (Q); the rest on results across the meets (q). Null = all on results. */
	advance_by_place: number | null;
	awards_medals: boolean;
	/** Every meet of the phase, a team tie's rubbers included — the number that blocks a delete. */
	meets_count: number;
	/** Distinct competitors holding a slot in one of the phase's meets. */
	competitors_count: number;
	created_at: string;
	updated_at: string;
}

// ─── Meets: requests ─────────────────────────────────────────────────────────

/**
 * One meet to create. On a create `null` and an absent key mean the same:
 * "not set". `sequence` omitted = the highest among its siblings + 1 (siblings
 * = the phase's top-level meets, or the other rubbers of the same tie), in the
 * order the meets are listed. `schedule_status` omitted = TENTATIVE.
 *
 * `parent_meet_id` makes this a rubber of a team tie: the parent must be a
 * top-level meet of the SAME phase (a rubber cannot have rubbers). In
 * `POST /meets/bulk` the parent may be created by the same call.
 */
const makeMeetCreateShape = (m: MeetFormMessages) => ({
	meet_id: clientIdField,
	phase_id: idField,
	parent_meet_id: idField.nullable().optional(),
	meet_no: makeMeetNoField(m),
	name: makeNameField(m, MEET_NAME_MAX).nullable().optional(),
	sequence: meetSequenceField.optional(),
	round_no: roundNoField.nullable().optional(),
	pool: makePoolField(m).nullable().optional(),
	scheduled_start: makeTimestampField(m).nullable().optional(),
	scheduled_end: makeTimestampField(m).nullable().optional(),
	venue_id: idField.nullable().optional(),
	schedule_status: z.enum(SCHEDULE_STATUSES).optional(),
	/** The meet's OWN overrides of the event's scoring — null / absent = "as the event says". */
	result_family: z.enum(RESULT_FAMILIES).nullable().optional(),
	result_type: z.enum(RESULT_TYPES).nullable().optional(),
	lower_is_better: z.boolean().nullable().optional(),
	remarks: makeRemarksField(m).nullable().optional(),
	/**
	 * The meet's `config` from the start (a decathlon's discipline meets carry
	 * `combined`), so a generated set of meets is ONE request, not one
	 * settings call per meet. Needs `result_family` on the same meet and is
	 * checked against that family's config schema here; later changes go
	 * through `PUT /meets/:id/settings`.
	 */
	config: z.record(z.unknown()).optional(),
});

type MeetCreateShape = z.infer<z.ZodObject<ReturnType<typeof makeMeetCreateShape>>>;

const meetCreateChecks = (m: MeetFormMessages) => (v: MeetCreateShape, ctx: z.RefinementCtx) => {
	if (v.meet_id && v.parent_meet_id && v.meet_id === v.parent_meet_id) {
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["parent_meet_id"], message: m.parentIsSelf });
	}
	if (endsBeforeStart(v.scheduled_start, v.scheduled_end)) {
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduled_end"], message: m.endBeforeStart });
	}
	if (v.config !== undefined) {
		if (!v.result_family) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["config"], message: m.configNeedsFamily });
		} else {
			const parsed = meetConfigSchema(v.result_family).safeParse(v.config);
			if (!parsed.success) for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: ["config", ...issue.path] });
		}
	}
};

/** `POST /meets` — 201 with the `MeetRow`; a repeat with the same `meet_id` is 200 and writes nothing. */
export function makeCreateMeetBody(m: MeetFormMessages = enMeetFormMessages) {
	return z.object({ event_id: idField, ...makeMeetCreateShape(m) }).superRefine(meetCreateChecks(m));
}
export const createMeetBody = makeCreateMeetBody();
export type CreateMeetBody = z.input<typeof createMeetBody>;

/**
 * `POST /meets/bulk` — up to `MEETS_BULK_MAX` meets of ONE event, all or
 * nothing. A meet whose `meet_id` already exists in the event is reported as
 * `existing` and left untouched, so the same call twice is safe.
 */
export function makeBulkCreateMeetsBody(m: MeetFormMessages = enMeetFormMessages) {
	return z.object({
		event_id: idField,
		meets: z
			.array(z.object(makeMeetCreateShape(m)).superRefine(meetCreateChecks(m)))
			.min(1)
			.max(MEETS_BULK_MAX)
			.superRefine((meets, ctx) => {
				const ids = meets.flatMap((x) => (x.meet_id ? [x.meet_id] : []));
				if (repeated(ids)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.meetIdRepeated });
				if (repeated(meets.map((x) => x.meet_no))) {
					ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.meetNoRepeated });
				}
			}),
	});
}
export const bulkCreateMeetsBody = makeBulkCreateMeetsBody();
export type BulkCreateMeetsBody = z.input<typeof bulkCreateMeetsBody>;

/**
 * `PATCH /meets/:id`. `version` is the one the client LOADED: a mismatch is
 * 409 `STALE_VERSION` and nothing is written; a real change bumps it by one.
 * `null` clears a nullable field; an absent key is left alone.
 *
 * `result_status` may only move among NONE, START_LIST, LIVE and UNOFFICIAL,
 * and only while the meet is not OFFICIAL — making a meet official and
 * reopening it have endpoints of their own; asking for either here is 409
 * CONFLICT `{ field: "result_status" }`. START_LIST, LIVE and UNOFFICIAL need
 * at least one filled slot.
 *
 * `result_family`, `result_type` and `lower_is_better` are the meet's OWN
 * overrides (null = as the event says). Once the meet holds results — a saved
 * result, a slot or line-up `detail`, or a `config` / `detail` of its own —
 * an edit that changes the EFFECTIVE value (the override, else the event's)
 * is 409 CONFLICT `{ field: "result_family" | "result_type" |
 * "lower_is_better", in_use: "results" }`, naming the first one that would
 * change. Setting an override equal to the inherited value, or clearing one
 * equal to the event's, changes nothing effective and is allowed.
 *
 * `event_id`, `phase_id` and `parent_meet_id` cannot be changed: a meet in the
 * wrong place is deleted and created again.
 */
export function makeUpdateMeetBody(m: MeetFormMessages = enMeetFormMessages) {
	return z
		.object({
			version: versionField,
			meet_no: makeMeetNoField(m).optional(),
			name: makeNameField(m, MEET_NAME_MAX).nullable().optional(),
			sequence: meetSequenceField.optional(),
			round_no: roundNoField.nullable().optional(),
			pool: makePoolField(m).nullable().optional(),
			scheduled_start: makeTimestampField(m).nullable().optional(),
			scheduled_end: makeTimestampField(m).nullable().optional(),
			actual_start: makeTimestampField(m).nullable().optional(),
			actual_end: makeTimestampField(m).nullable().optional(),
			venue_id: idField.nullable().optional(),
			schedule_status: z.enum(SCHEDULE_STATUSES).optional(),
			result_status: z.enum(RESULT_STATUSES).optional(),
			result_family: z.enum(RESULT_FAMILIES).nullable().optional(),
			result_type: z.enum(RESULT_TYPES).nullable().optional(),
			lower_is_better: z.boolean().nullable().optional(),
			remarks: makeRemarksField(m).nullable().optional(),
			/**
			 * The playing area typed by the operator (0088) — used when the meet's
			 * venue is not a court / ring / mat set up under the hall. `null` clears it.
			 */
			area_name: z.string().trim().min(1).max(MEET_AREA_NAME_MAX).nullable().optional(),
			/**
			 * The meet's video link (live video, 0086): contracts `parseStreamUrl`
			 * decides — an allowlist, stored canonical; `""` or `null` clears it.
			 * The English messages: `enStreamFormMessages`.
			 */
			stream_url: makeStreamUrlField().optional(),
			/** Sorts the meet first among the portal's live videos. */
			stream_featured: z.boolean().optional(),
			/** The headline for this change in the Activity feed. */
			reason: reasonText.optional(),
		})
		.superRefine((v, ctx) => {
			const { version: _version, reason: _reason, ...fields } = v;
			if (!Object.values(fields).some((f) => f !== undefined)) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.nothingToChange });
			}
			if (endsBeforeStart(v.scheduled_start, v.scheduled_end)) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduled_end"], message: m.endBeforeStart });
			}
			if (endsBeforeStart(v.actual_start, v.actual_end)) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["actual_end"], message: m.endBeforeStart });
			}
		});
}
export const updateMeetBody = makeUpdateMeetBody();
export type UpdateMeetBody = z.input<typeof updateMeetBody>;

export const MEET_BULK_ACTIONS = ["DELETE", "SET_SCHEDULE_STATUS"] as const;
export type MeetBulkAction = (typeof MEET_BULK_ACTIONS)[number];

/**
 * `POST /meets/bulk-action` — SUPER_ADMIN / ADMIN, confirmed with the actor's
 * OWN password (`confirm_password`; a wrong one is 403
 * CONFIRMATION_PASSWORD_INVALID and nothing changes). `schedule_status` is
 * required for SET_SCHEDULE_STATUS.
 */
export function makeMeetBulkActionBody(m: MeetFormMessages = enMeetFormMessages) {
	return z
		.object({
			action: z.enum(MEET_BULK_ACTIONS),
			meet_ids: z
				.array(idField)
				.min(1)
				.max(MEET_BULK_ACTION_MAX)
				.refine((ids) => !repeated(ids), { message: m.meetIdSelectedTwice }),
			schedule_status: z.enum(SCHEDULE_STATUSES).optional(),
			/** Not trimmed: a password may begin or end with a space. */
			confirm_password: z.string().min(1, m.confirmPasswordRequired).max(200),
			reason: reasonText.optional(),
		})
		.superRefine((v, ctx) => {
			if (v.action === "SET_SCHEDULE_STATUS" && v.schedule_status === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["schedule_status"], message: m.scheduleStatusRequired });
			}
		});
}
export const meetBulkActionBody = makeMeetBulkActionBody();
export type MeetBulkActionBody = z.input<typeof meetBulkActionBody>;

/**
 * `DELETE /meets/:id?reason=&discard_live=` — `reason` is the audit headline;
 * `discard_live=true` says the caller was shown that TSR holds live scores
 * for the meet (`GET /meets/:id/tsr-live`) and deletes them with it. Without
 * it a meet with TSR live state is refused (409 `{ tsr_live: true }`).
 */
export const deleteMeetQuery = z.object({
	reason: reasonText.optional(),
	discard_live: z.enum(["true", "false"]).optional(),
});
export type DeleteMeetQuery = z.input<typeof deleteMeetQuery>;

/**
 * `PUT /meets/:id/officials` — the meet's WHOLE official list: officials not
 * listed are removed, the rest are created or updated (`meet_official_id`
 * names an existing one). Each needs a `people_id` or a typed `name`.
 * `position` is the official's seat, unique within the meet. A coach or
 * manager names their team's slot in `meet_competitor_id`, which must be a slot
 * of THIS meet. `sort_order` omitted = the official's place in the list.
 *
 * `version` (optional) is the MEET's version as the client loaded it — the
 * same field as `PATCH /meets/:id`: another version is 409 `STALE_VERSION`
 * and nothing is written. Omitted = no check (older clients). The officials
 * are part of the meet's state, so a save that changes the list bumps the
 * meet's `version` by one (an identical list changes nothing and bumps
 * nothing); the response is still the list — read the meet again
 * (`GET /meets/:id`) for its new version.
 */
export function makeMeetOfficialsBody(m: MeetFormMessages = enMeetFormMessages) {
	const official = z
		.object({
			meet_official_id: clientIdField,
			people_id: idField.nullable().optional(),
			name: z.string().trim().min(1, m.officialWhoRequired).max(MEET_OFFICIAL_NAME_MAX, m.officialNameTooLong).nullable().optional(),
			role: z.enum(OFFICIAL_ROLES),
			position: z.string().trim().min(1).max(MEET_OFFICIAL_POSITION_MAX, m.positionTooLong).nullable().optional(),
			meet_competitor_id: idField.nullable().optional(),
			sort_order: z.number().int().min(0).max(PG_SMALLINT_MAX).optional(),
		})
		.refine((o) => Boolean(o.people_id) || Boolean(o.name), { path: ["name"], message: m.officialWhoRequired });
	return z.object({
		version: versionField.optional(),
		officials: z
			.array(official)
			.max(MEET_OFFICIALS_MAX)
			.superRefine((list, ctx) => {
				if (repeated(list.flatMap((o) => (o.meet_official_id ? [o.meet_official_id] : [])))) {
					ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.officialIdRepeated });
				}
				if (repeated(list.flatMap((o) => (o.position ? [o.position] : [])))) {
					ctx.addIssue({ code: z.ZodIssueCode.custom, message: m.positionRepeated });
				}
			}),
	});
}
export const meetOfficialsBody = makeMeetOfficialsBody();
export type MeetOfficialsBody = z.input<typeof meetOfficialsBody>;

// ─── Meets: list queries ─────────────────────────────────────────────────────

const splitList = competitionSplitList;

/** `schedule_status=SCHEDULED,DELAYED` — one or more, comma-separated. */
const scheduleStatusList = z.string().max(200).transform(splitList).pipe(z.array(z.enum(SCHEDULE_STATUSES)).min(1));
/** `result_status=LIVE,UNOFFICIAL` — one or more, comma-separated. */
const resultStatusList = z.string().max(200).transform(splitList).pipe(z.array(z.enum(RESULT_STATUSES)).min(1));

/**
 * A yes/no query parameter: exactly `true` or `false`, read as a boolean. Never
 * `z.coerce.boolean()` — `Boolean("false")` is true (CLAUDE.md, bug 2).
 */
const booleanQueryParam = z.enum(["true", "false"]).transform((v) => v === "true");

/**
 * The filters every schedule read shares. Each is optional and they combine
 * with AND. `venue_id` matches that venue AND every venue inside it (a hall's
 * courts). Status filters take a comma-separated list.
 *
 * `medal_events=true` keeps only the meets that DECIDE MEDALS — a meet in a
 * phase that `awards_medals` of an event that `is_medal_event` (a
 * demonstration event never awards); `false` keeps only the others. One
 * definition, in SQL (`meetDecidesMedalsSql`, apps/api meets.queries).
 *
 * `meet_no=401` keeps the meets numbered exactly that (trimmed, case as
 * stored). A meet number is unique within its event only, so without an
 * event it matches that number in every event the other filters keep.
 */
const meetFilterShape = {
	games_id: idField.optional(),
	sport_id: idField.optional(),
	discipline_id: idField.optional(),
	event_id: idField.optional(),
	phase_id: idField.optional(),
	venue_id: idField.optional(),
	schedule_status: scheduleStatusList.optional(),
	result_status: resultStatusList.optional(),
	medal_events: booleanQueryParam.optional(),
	// .trim() FIRST, so "   " is refused rather than read as "" (CLAUDE.md, bug 5).
	meet_no: z.string().trim().min(1).max(MEET_NO_MAX).optional(),
};

/** Days in the games' own `timezone`, `from` inclusive, `to` exclusive — see `competitionDayRangeShape` (common.ts). */
const dayRangeShape = competitionDayRangeShape;

const fromBeforeTo = competitionFromBeforeTo;

/**
 * The schedule's keyset cursor: `<scheduled_start as UTC text, microseconds>_<meet_id>`.
 * Opaque to clients — send back `next_cursor` exactly as received. The time is
 * Postgres' own full-precision text, never a JS `Date`: a millisecond cursor
 * skips meets that share a millisecond (CLAUDE.md, bug 6).
 */
export const MEET_CURSOR_PATTERN =
	/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z)_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
const strictUtcInstant = z.string().datetime();
const meetCursorField = z
	.string()
	.max(100)
	.regex(MEET_CURSOR_PATTERN, "Malformed cursor")
	.refine((v) => notYearZero(v) && strictUtcInstant.safeParse(v.slice(0, v.lastIndexOf("_"))).success, "Malformed cursor");

export const MEET_LIST_DIRECTIONS = ["asc", "desc"] as const;
export type MeetListDirection = (typeof MEET_LIST_DIRECTIONS)[number];

/**
 * `GET /meets` — the schedule: top-level meets that HAVE a `scheduled_start`
 * (rubbers are shown inside their tie), keyset on `(scheduled_start, meet_id)`.
 * Response: `Keyset<MeetListItem>`.
 */
export const listMeetsQuery = keysetQuery
	.extend({
		cursor: meetCursorField.optional(),
		dir: z.enum(MEET_LIST_DIRECTIONS).default("asc"),
		...meetFilterShape,
		...dayRangeShape,
	})
	.superRefine(fromBeforeTo);
export type ListMeetsQuery = z.input<typeof listMeetsQuery>;

/**
 * `GET /meets/unscheduled` — top-level meets with NO `scheduled_start`, in
 * programme order (sport, discipline, event, phase, meet). Offset pages:
 * `Paginated<MeetListItem>`. No `from` / `to`: an unscheduled meet has no day.
 */
export const listUnscheduledMeetsQuery = paginationQuery.extend(meetFilterShape);
export type ListUnscheduledMeetsQuery = z.input<typeof listUnscheduledMeetsQuery>;

/**
 * `GET /meets/status-counts` — how many top-level meets the filters match, per
 * (schedule_status, result_status). Without `from` / `to` that is every one of
 * them, scheduled or not — what `GET /meets` and `GET /meets/unscheduled` list
 * together; with a day range, only meets scheduled in it.
 */
export const meetStatusCountsQuery = z.object({ ...meetFilterShape, ...dayRangeShape }).superRefine(fromBeforeTo);
export type MeetStatusCountsQuery = z.input<typeof meetStatusCountsQuery>;

/** `GET /meets/status-counts/by-sport` — a games has tens of sports, so one page normally holds them all. */
export const MEET_SPORT_COUNTS_PAGE_SIZE_DEFAULT = PAGE_SIZE_MAX;

/**
 * `GET /meets/status-counts/by-sport` — the dashboard: every sport of ONE
 * games (`games_id` is required), each with its meets counted per
 * (schedule_status, result_status) exactly as `GET /meets/status-counts`
 * counts them — top-level meets only; without `from` / `to` every one of
 * them, with a day range only meets scheduled in it. Sports with no meet in
 * the range are listed with `total: 0`. Ordered like the programme (sport
 * `sort_order`, then `code`). Offset pages, `Paginated<MeetSportStatusCounts>`.
 */
export const meetStatusCountsBySportQuery = z
	.object({
		games_id: idField,
		...dayRangeShape,
		page: paginationQuery.shape.page,
		page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(MEET_SPORT_COUNTS_PAGE_SIZE_DEFAULT),
	})
	.superRefine(fromBeforeTo);
export type MeetStatusCountsBySportQuery = z.input<typeof meetStatusCountsBySportQuery>;

/** `GET /meets/days` — a games lasts weeks, so one page normally holds every day. */
export const MEET_DAYS_PAGE_SIZE_DEFAULT = PAGE_SIZE_MAX;

/**
 * `GET /meets/days` — the DAYS on which ONE games (`games_id` is required) has
 * scheduled top-level meets matching the filters, each with how many: what
 * `GET /meets` lists for `from=<day>&to=<day + 1>` with the same filters. A
 * day is a calendar day in the games' own `timezone` — the same cut as
 * `from` / `to`. Ascending, offset pages: `Paginated<MeetDayCount>`.
 * Unscheduled meets have no day and are not counted.
 */
export const meetDaysQuery = z.object({
	...meetFilterShape,
	games_id: idField,
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(MEET_DAYS_PAGE_SIZE_DEFAULT),
});
export type MeetDaysQuery = z.input<typeof meetDaysQuery>;

/**
 * A meet is FINISHED — nothing more will happen in it — when its result is
 * OFFICIAL, or when it was CANCELLED or POSTPONED. One definition: the
 * dashboard's `next_start_at` skips these, and a timeline's "next meet" should
 * use `isMeetFinished` rather than its own copy (CLAUDE.md, bug 4).
 */
export const MEET_FINISHED_RESULT_STATUSES = ["OFFICIAL"] as const satisfies readonly ResultStatus[];
export const MEET_FINISHED_SCHEDULE_STATUSES = ["CANCELLED", "POSTPONED"] as const satisfies readonly ScheduleStatus[];

export function isMeetFinished(m: { result_status: ResultStatus; schedule_status: ScheduleStatus }): boolean {
	return (
		(MEET_FINISHED_RESULT_STATUSES as readonly ResultStatus[]).includes(m.result_status) ||
		(MEET_FINISHED_SCHEDULE_STATUSES as readonly ScheduleStatus[]).includes(m.schedule_status)
	);
}

// ─── Meets: responses ────────────────────────────────────────────────────────

/** What a meet create or patch returns: the row itself, overrides as stored. */
export interface MeetRow {
	meet_id: string;
	event_id: string;
	phase_id: string;
	/** The tie this rubber belongs to. Null for every ordinary meet. */
	parent_meet_id: string | null;
	meet_no: string;
	name: string | null;
	sequence: number;
	round_no: number | null;
	pool: string | null;
	scheduled_start: string | null;
	scheduled_end: string | null;
	actual_start: string | null;
	actual_end: string | null;
	venue_id: string | null;
	schedule_status: ScheduleStatus;
	result_status: ResultStatus;
	/** When `result_status` last changed. */
	result_status_at: string | null;
	/** The meet's OWN overrides — null = "as the event says". */
	result_family: ResultFamily | null;
	result_type: ResultType | null;
	lower_is_better: boolean | null;
	remarks: string | null;
	/** The playing area typed for this meet (0088) — shown when the venue is not a court inside a hall. */
	area_name: string | null;
	/** The video link, canonical (contracts `parseStreamUrl`); null = none. */
	stream_url: string | null;
	/** Sorts first among the portal's live videos. */
	stream_featured: boolean;
	version: number;
	created_at: string;
	updated_at: string;
}

/** A `MeetRow` whose scoring fields are the EFFECTIVE values: the meet's override, else the event's. */
export interface MeetRowEffective extends Omit<MeetRow, "result_family" | "result_type" | "lower_is_better"> {
	result_family: ResultFamily;
	result_type: ResultType;
	lower_is_better: boolean;
}

/**
 * Where an unfilled slot's competitor will come from — "winner of QF1"
 * (`meet_id` + `outcome`), "2nd of Heat 3" (`meet_id` + `rank`), "1st of Group
 * A" (`phase_id` + `pool` + `rank`). Each key is null when it does not apply.
 */
export interface SlotSource {
	meet_id: string | null;
	/** The source meet's number, so a bracket can say "Winner of 204" without another read. */
	meet_no: string | null;
	outcome: SlotSourceOutcome | null;
	phase_id: string | null;
	pool: string | null;
	rank: number | null;
}

/** One slot in the schedule's summary of a meet. */
export interface MeetCompetitorBrief {
	meet_competitor_id: string;
	competitor_id: string | null;
	/** The competitor's display name (`CompetitorRef.name`); null while the slot is unfilled. */
	name: string | null;
	/** The IOC code of the organization's country — MAS, THA. */
	noc_code: string | null;
	flag_url: string | null;
	side: string | null;
	rank: number | null;
	outcome: MeetOutcome | null;
	irm: IrmCode | null;
	result_display: string | null;
	/** Set only while the slot is unfilled. */
	source: SlotSource | null;
}

export interface MeetEventSummary {
	event_id: string;
	code: string;
	name: string;
	gender: EventGender;
	competitor_type: CompetitorType;
}

export interface MeetDisciplineSummary {
	discipline_id: string;
	code: string;
	name: string;
	/** What the two sides of a head-to-head are called; SEQUENCE = no names, the start order decides (1 on top). */
	side_scheme: SideScheme;
}

export interface MeetSportSummary {
	sport_id: string;
	code: string;
	name: string;
	/** The games the sport belongs to (`games.code`, SEAG / APG): the same code can name a sport in both. */
	games_code: string;
}

export interface MeetPhaseSummary {
	phase_id: string;
	type: PhaseType;
	name: string;
	awards_medals: boolean;
}

/** Where a meet is played. `parent_name` is the building when the venue is a court or mat inside one. */
export interface MeetVenueSummary {
	venue_id: string;
	code: string;
	name: string;
	parent_name: string | null;
}

/** One row of the schedule — `GET /meets`, `GET /meets/unscheduled`. */
export interface MeetListItem {
	meet_id: string;
	meet_no: string;
	name: string | null;
	sequence: number;
	round_no: number | null;
	pool: string | null;
	scheduled_start: string | null;
	scheduled_end: string | null;
	schedule_status: ScheduleStatus;
	result_status: ResultStatus;
	/** When `result_status` last changed (null before the first change) — "unofficial for 40 min". */
	result_status_at: string | null;
	version: number;
	event: MeetEventSummary;
	discipline: MeetDisciplineSummary;
	sport: MeetSportSummary;
	phase: MeetPhaseSummary;
	/**
	 * This meet's place among its phase's top-level meets, 1-based, in the
	 * order `GET /events/:id/structure` lists them (`sequence`, then `meet_no`)
	 * — "Heat 2 of 3" is `phase_position` of `phase_meets_count`.
	 */
	phase_position: number;
	/** Top-level meets in the phase (a team tie's rubbers are not counted, unlike `PhaseItem.meets_count`). */
	phase_meets_count: number;
	venue: MeetVenueSummary | null;
	/** Slots in the meet, filled or not. */
	competitor_count: number;
	/** At most `MEET_BRIEF_COMPETITORS_MAX`, ordered by rank, then start order. */
	competitors: MeetCompetitorBrief[];
}

/** `GET /meets/status-counts` — one row per combination that occurs. */
export interface MeetStatusCount {
	schedule_status: ScheduleStatus;
	result_status: ResultStatus;
	count: number;
}

/** One row of `GET /meets/days`. */
export interface MeetDayCount {
	/** `YYYY-MM-DD`, a calendar day in the games' `timezone`. */
	day: string;
	/** Scheduled top-level meets that start on that day and match the filters. */
	count: number;
}

/** One row of `GET /meets/status-counts/by-sport`: one sport of the games. */
export interface MeetSportStatusCounts {
	sport: MeetSportSummary;
	/** Top-level meets counted — the sum of `counts`. */
	total: number;
	/** The same rows as `GET /meets/status-counts`, for this sport: one per combination that occurs, enum order. `[]` when `total` is 0. */
	counts: MeetStatusCount[];
	/**
	 * The earliest `scheduled_start` at or after NOW among the counted meets
	 * that are not finished (`isMeetFinished`) — so within the day range when
	 * one is given. Null when there is none.
	 */
	next_start_at: string | null;
}

/** One member of a slot's line-up for THIS meet. A name and a role — no identity data. */
export interface MeetSlotLineupMember {
	meet_lineup_id: string;
	competitor_member_id: string;
	people_id: string;
	name: string;
	role: MemberRole;
	/** The member's bib on the roster. */
	bib: string | null;
	/** Relay leg, board, batting order. */
	sort_order: number | null;
	position: string | null;
	is_starter: boolean;
	is_captain: boolean;
	detail: Record<string, unknown>;
}

/** One slot of a meet, with its result — what a results page or an OVR scoring form shows. */
export interface MeetSlot {
	meet_competitor_id: string;
	version: number;
	start_order: number | null;
	lane: string | null;
	side: string | null;
	/** This meet's bib — weightlifting's lot number. */
	bib: string | null;
	/**
	 * The athlete's own bib for the event, the same in every meet of it: the
	 * entry's (OVR's entry page sets it), else an individual's roster bib.
	 * Null without a competitor or a bib.
	 * What keys an athlete for a device is `slotBib`: this meet's `bib` first.
	 */
	entry_bib: string | null;
	/** The entry for this meet — weightlifting's entry total. */
	entry_mark: string | null;
	/** Weighed in for this meet, kilograms — weightlifting. */
	bodyweight_kg: number | null;
	/** Null while the slot waits for its source to be decided. */
	competitor: CompetitorRef | null;
	/** Where the slot is filled from — kept after it is filled, so the bracket can still be drawn. */
	source: SlotSource | null;
	irm: IrmCode | null;
	result_value: number | null;
	result_display: string | null;
	points: number | null;
	rank: number | null;
	outcome: MeetOutcome | null;
	records: string[];
	detail: Record<string, unknown>;
	result_updated_at: string | null;
	/** Who wrote the result: the timing feed, an operator, or an operator over the feed (`RESULT_SOURCES`). Null = no result yet. */
	result_source: ResultSource | null;
	/** How the competitor went through from THIS phase (`phase_competitors`), else null. */
	qualification: QualificationMark | null;
	/** The competitor's OFFICIAL medal in this event (`event_medals` only), else null. `phase.awards_medals` says whether this meet decides it. */
	medal: Medal | null;
	lineup: MeetSlotLineupMember[];
}

/**
 * The bib that keys an athlete for a venue device (a field console's card, a
 * bib-keyed timer) and on the console start list: this meet's own `bib`, else
 * the athlete's for the event (`entry_bib`) — so a bib typed once on the
 * entry follows the athlete into every meet. The API matches Apply on the
 * same pair (`lockTimingSlots`). Null when neither is set.
 */
export function slotBib(slot: Pick<MeetSlot, "bib" | "entry_bib">): string | null {
	const own = slot.bib?.trim() ?? "";
	if (own !== "") return own;
	const entry = slot.entry_bib?.trim() ?? "";
	return entry === "" ? null : entry;
}

/** One official of a meet. `name` is the person's printed name, or the typed name of an official who is not a registered person. */
export interface MeetOfficialItem {
	meet_official_id: string;
	people_id: string | null;
	name: string;
	role: OfficialRole;
	position: string | null;
	/** The slot (team) a coach or manager is with. Null for neutral officials. */
	meet_competitor_id: string | null;
	sort_order: number;
}

/** A rubber of a team tie, inside its tie's detail. Scoring fields are EFFECTIVE, as in the tie itself. */
export interface MeetChild extends MeetRowEffective {
	config: Record<string, unknown>;
	detail: Record<string, unknown>;
	competitors: MeetSlot[];
}

export interface MeetDetailEvent {
	event_id: string;
	code: string;
	name: string;
	name_translations: Record<string, string>;
	gender: EventGender;
	competitor_type: CompetitorType;
	/**
	 * A TEAM event's largest roster, else null. 2 = a pair (doubles), not a
	 * team that plays a tie of rubbers — the public feed's `pair` uses the same test.
	 */
	team_size_max: number | null;
	/** Set while the event is locked: every write beneath it is refused. */
	locked_at: string | null;
}

export interface MeetGamesSummary {
	games_id: string;
	code: string;
	/** The IANA zone the games' days are counted in. */
	timezone: string;
}

export interface MeetDetailPhase {
	phase_id: string;
	type: PhaseType;
	name: string;
	sequence: number;
	standing_rule: StandingRule;
	awards_medals: boolean;
}

/**
 * `GET /meets/:id` — everything a results page or an OVR form needs, in one
 * read. `result_family`, `result_type` and `lower_is_better` are EFFECTIVE
 * values here (the meet's override, else the event's), in the children too;
 * once the meet holds results they no longer change. A save answered 409
 * STALE_VERSION with `details.result_family` means the family changed after
 * this read: read the meet again, its form depends on the family.
 * Slots are in start order.
 */
export interface MeetDetail extends MeetChild {
	event: MeetDetailEvent;
	discipline: MeetDisciplineSummary;
	sport: MeetSportSummary;
	games: MeetGamesSummary;
	phase: MeetDetailPhase;
	venue: MeetVenueSummary | null;
	officials: MeetOfficialItem[];
	/** The rubbers of a team tie, in sequence. Empty for an ordinary meet. */
	children: MeetChild[];
	/** The timing devices that have written this meet, and the lanes they left unapplied (0030). Empty without a feed. */
	timing_feeds: MeetTimingFeed[];
}

/** `POST /meets/bulk`. */
export interface BulkCreateMeetsResult {
	/** Meets created by this call, in the order they were listed. */
	created: string[];
	/** Meets whose `meet_id` already existed in the event — left exactly as they were. */
	existing: string[];
}

/**
 * Why `POST /meets/bulk-action` left a meet alone:
 *
 *   NOT_FOUND     no such meet
 *   LOCKED        its event is locked
 *   TSR_LIVE      TSR holds live scores for it (or a rubber) that a delete would
 *                 throw away; the single delete warns and asks instead
 *   HAS_MEDALS    an awarded medal points at one of its slots, a rubber's or its tie's
 *   IS_SOURCE     a later meet it placed a competitor in has already started
 *                 (or holds a result) — reopen's rule
 *   UNCHANGED     already in the requested schedule status
 *   CHANGED       it changed while the action ran — load it again
 *
 * Results, rubbers and places still waiting on a meet no longer hold it: a
 * delete takes them with it (user, 2026-10-30).
 */
export const MEET_BULK_SKIP_REASONS = ["NOT_FOUND", "LOCKED", "TSR_LIVE", "HAS_MEDALS", "IS_SOURCE", "UNCHANGED", "CHANGED"] as const;
export type MeetBulkSkipReason = (typeof MEET_BULK_SKIP_REASONS)[number];

/**
 * `GET /meets/:id/tsr-live` — whether TSR holds live scores for the meet or
 * one of its rubbers. The delete dialog asks when it opens and warns; the
 * state has no "saved" mark, so true means "scored in TSR lately".
 */
export interface MeetTsrLiveResult {
	live: boolean;
}

/** 409 CONFLICT `details` when `DELETE /meets/:id` without `discard_live=true` meets TSR live state. Nothing was written. */
export interface MeetDeleteTsrLiveDetails {
	tsr_live: true;
}

/** `POST /meets/bulk-action`. A meet the action cannot apply to is skipped, not an error. */
export interface MeetBulkActionResult {
	done: string[];
	skipped: Array<{ meet_id: string; why: MeetBulkSkipReason }>;
}

// ─── Event structure ─────────────────────────────────────────────────────────

export interface EventStructureEvent {
	event_id: string;
	code: string;
	name: string;
	name_translations: Record<string, string>;
	gender: EventGender;
	competitor_type: CompetitorType;
	result_family: ResultFamily;
	result_type: ResultType;
	lower_is_better: boolean;
	is_medal_event: boolean;
	locked_at: string | null;
	discipline: MeetDisciplineSummary;
	sport: MeetSportSummary;
	games: MeetGamesSummary;
}

/** A top-level meet in the structure, with EVERY slot (no cap). */
export interface EventStructureMeet {
	meet_id: string;
	parent_meet_id: string | null;
	meet_no: string;
	name: string | null;
	sequence: number;
	round_no: number | null;
	pool: string | null;
	scheduled_start: string | null;
	schedule_status: ScheduleStatus;
	result_status: ResultStatus;
	version: number;
	/** Rubbers of this tie (they are not listed here; `GET /meets/:id` has them). */
	children_count: number;
	competitors: MeetCompetitorBrief[];
}

export interface EventStructurePhase extends PhaseItem {
	/** Top-level meets, in sequence. */
	meets: EventStructureMeet[];
}

/**
 * `GET /events/:id/structure` — the whole event in one read: what OVR draws a
 * bracket or a heat sheet from. Phases in sequence, each with its top-level
 * meets in sequence.
 */
export interface EventStructure {
	event: EventStructureEvent;
	phases: EventStructurePhase[];
}
